/*
 * <custom-stacked-product>
 * Variant selection + add-to-cart for the stacked product gallery.
 * No external libraries. Progressive enhancement: the <form> still posts
 * normally to /cart/add if JavaScript is unavailable.
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class CustomStackedProduct extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-csp-form]');
      this.idInput = this.querySelector('[data-csp-variant-id]');
      this.priceTarget = this.querySelector('[data-csp-price]');
      this.addButton = this.querySelector('[data-csp-add]');
      this.addText = this.querySelector('[data-csp-add-text]');
      this.errorTarget = this.querySelector('[data-csp-error]');
      this.skuTarget = this.querySelector('[data-csp-sku]');
      this.scrollToVariant = this.getAttribute('data-scroll-to-variant') === 'true';

      this.variants = this.readVariants();
      if (!this.variants.length) return;

      this.optionInputs = Array.from(this.querySelectorAll('[data-csp-option-input]'));
      this.onOptionChange = this.onOptionChange.bind(this);
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange);
      });

      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      this.setupSizeGuide();

      this.setupCarousel();

      // Reflect the initial selection without scrolling on first paint.
      this.updateSelection({ scroll: false });
    }

    readVariants() {
      const node = this.querySelector('[data-csp-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    /* Currently selected option values, ordered by option position. */
    selectedOptions() {
      const values = [];
      this.optionInputs.forEach((input) => {
        if (input.type === 'radio' && !input.checked) return;
        const pos = parseInt(input.getAttribute('data-option-position'), 10);
        values[pos - 1] = input.value;
      });
      return values;
    }

    findVariant(selected) {
      return this.variants.find((variant) =>
        variant.options.every((opt, i) => opt === selected[i])
      );
    }

    onOptionChange() {
      this.updateSelection({ scroll: this.scrollToVariant });
    }

    updateSelection(opts) {
      opts = opts || {};
      // Products with only a default variant have no option inputs.
      const variant = this.optionInputs.length
        ? this.findVariant(this.selectedOptions())
        : this.variants[0];

      this.updateSelectedValueLabels();

      if (!variant) {
        this.setButton({ disabled: true, text: this.addButton.getAttribute('data-unavailable-text') || 'Unavailable' });
        return;
      }

      // Variant id (the field actually submitted to the cart).
      if (this.idInput) this.idInput.value = variant.id;

      // Price.
      if (this.priceTarget) {
        this.priceTarget.innerHTML = this.priceMarkup(variant);
      }

      // SKU.
      if (this.skuTarget) this.skuTarget.textContent = variant.sku || '';

      // Availability / button state.
      if (variant.available) {
        this.setButton({ disabled: false, text: this.addButton.getAttribute('data-add-text') || 'Add to Shopping Bag' });
      } else {
        this.setButton({ disabled: true, text: this.addButton.getAttribute('data-soldout-text') || 'Sold out' });
      }

      // URL ?variant= parameter.
      this.updateUrl(variant.id);

      // Scroll the gallery to the variant's featured media.
      if (opts.scroll && variant.featured_media_id) {
        this.scrollToMedia(variant.featured_media_id);
      }

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
      );
    }

    updateSelectedValueLabels() {
      // Mirror each chosen value next to its option legend (e.g. the colour name).
      this.querySelectorAll('[data-selected-value-for]').forEach((label) => {
        const pos = label.getAttribute('data-selected-value-for');
        const input = this.querySelector(
          `[data-csp-option-input][data-option-position="${pos}"]:checked, ` +
          `input[type="hidden"][data-csp-option-input][data-option-position="${pos}"]`
        );
        if (input) label.textContent = input.value;
      });
    }

    priceMarkup(variant) {
      const current = `<span class="csp__price-current">${variant.price}</span>`;
      if (variant.compare_at_price && variant.compare_at_price !== variant.price && variant.compare_at_price_amount > variant.price_amount) {
        return `<span class="csp__price-compare">${variant.compare_at_price}</span>${current}`;
      }
      return current;
    }

    setButton(state) {
      if (!this.addButton) return;
      this.addButton.disabled = state.disabled;
      if (this.addText) this.addText.textContent = state.text;
    }

    updateUrl(variantId) {
      if (!window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    scrollToMedia(mediaId) {
      const target = this.ownerDocument.getElementById('CustomMedia-' + mediaId);
      if (!target) return;
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    /* ---------------------------------------------------------- Add to cart */

    onSubmit(event) {
      // Progressive enhancement: if fetch is unavailable, let the form post.
      if (!window.fetch) return;
      event.preventDefault();
      if (!this.form || this.addButton.disabled) return;

      this.clearError();
      this.setLoading(true);

      const formData = new FormData(this.form);
      const config = {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      };

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', config)
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            this.showError((data && data.description) || (data && data.message) || 'Could not add to cart.');
            return;
          }
          // Let the host theme update its cart UI if it listens for these.
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(
            new CustomEvent('product:added-to-cart', { bubbles: true, detail: { item: data } })
          );
          // Fallback when no cart drawer handles the event.
          if (this.getAttribute('data-redirect-to-cart') === 'true') {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(() => this.showError('Could not add to cart. Please try again.'))
        .finally(() => this.setLoading(false));
    }

    setLoading(isLoading) {
      if (!this.addButton) return;
      this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      this.addButton.classList.toggle('is-loading', isLoading);
    }

    showError(message) {
      if (this.errorTarget) {
        this.errorTarget.textContent = message;
      }
    }

    clearError() {
      if (this.errorTarget) this.errorTarget.textContent = '';
    }

    /* ------------------------------------------------ Size guide popup */

    setupSizeGuide() {
      this.sizeGuide = this.querySelector('[data-csp-sizeguide]');
      this.sizeGuideTrigger = this.querySelector('[data-csp-sizeguide-open]');
      if (!this.sizeGuide || !this.sizeGuideTrigger) return;

      this.sizeGuideTrigger.addEventListener('click', this.openSizeGuide.bind(this));

      const closeBtn = this.sizeGuide.querySelector('[data-csp-sizeguide-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeSizeGuide());

      // Click on the backdrop (the dialog element itself) closes the popup.
      this.sizeGuide.addEventListener('click', (event) => {
        if (event.target === this.sizeGuide) this.closeSizeGuide();
      });

      // Fires for Esc/cancel and programmatic close — restore scroll & focus.
      this.sizeGuide.addEventListener('close', () => {
        document.documentElement.classList.remove('csp-sizeguide-open');
        if (this.sizeGuideTrigger) this.sizeGuideTrigger.focus();
      });
    }

    openSizeGuide(event) {
      if (event) event.preventDefault();
      if (!this.sizeGuide) return;
      if (typeof this.sizeGuide.showModal === 'function') {
        this.sizeGuide.showModal();
      } else {
        this.sizeGuide.setAttribute('open', '');
      }
      document.documentElement.classList.add('csp-sizeguide-open');
    }

    closeSizeGuide() {
      if (!this.sizeGuide) return;
      if (typeof this.sizeGuide.close === 'function') {
        this.sizeGuide.close();
      } else {
        this.sizeGuide.removeAttribute('open');
        document.documentElement.classList.remove('csp-sizeguide-open');
        if (this.sizeGuideTrigger) this.sizeGuideTrigger.focus();
      }
    }

    /* ----------------------------------------- Optional carousel/slider nav */

    setupCarousel() {
      const scroller = this.querySelector('[data-csp-media-list]');
      if (!scroller) return;
      this.scroller = scroller;

      const prev = this.querySelector('[data-csp-prev]');
      const next = this.querySelector('[data-csp-next]');
      const items = () => Array.from(scroller.querySelectorAll('[data-csp-media]'));

      const scrollByItem = (dir) => {
        const list = items();
        if (!list.length) return;
        const width = list[0].getBoundingClientRect().width;
        scroller.scrollBy({ left: dir * width, behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      if (prev) prev.addEventListener('click', () => scrollByItem(-1));
      if (next) next.addEventListener('click', () => scrollByItem(1));

      const dots = Array.from(this.querySelectorAll('[data-csp-dot]'));
      if (dots.length) {
        dots.forEach((dot, i) => {
          dot.addEventListener('click', () => {
            const list = items();
            if (list[i]) {
              list[i].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', inline: 'start', block: 'nearest' });
            }
          });
        });

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const list = items();
                const idx = list.indexOf(entry.target);
                dots.forEach((d, di) => d.setAttribute('aria-current', di === idx ? 'true' : 'false'));
              });
            },
            { root: scroller, threshold: 0.6 }
          );
          items().forEach((item) => observer.observe(item));
        }
      }
    }
  }

  if (!customElements.get('custom-stacked-product')) {
    customElements.define('custom-stacked-product', CustomStackedProduct);
  }

  /* --------------------------------------- Mobile slider chevrons
   * The gallery (and its chevrons) live OUTSIDE the <custom-stacked-product>
   * element, so they're wired here against each .csp section. Chevrons scroll
   * the media list one image at a time and hide at each end.
   */
  function initSliderChevrons(root) {
    var sections = (root || document).querySelectorAll('.csp');
    Array.prototype.forEach.call(sections, function (section) {
      var list = section.querySelector('[data-csp-media-list]');
      var prev = section.querySelector('[data-csp-slider-prev]');
      var next = section.querySelector('[data-csp-slider-next]');
      if (!list || (!prev && !next) || list.dataset.cspChevronsReady) return;
      list.dataset.cspChevronsReady = '1';

      var step = function () {
        var item = list.querySelector('[data-csp-media]');
        if (!item) return list.clientWidth;
        var styles = window.getComputedStyle(list);
        var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return item.getBoundingClientRect().width + gap;
      };

      if (prev) {
        prev.addEventListener('click', function () {
          list.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          list.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      }

      var update = function () {
        var max = list.scrollWidth - list.clientWidth;
        if (prev) prev.disabled = list.scrollLeft <= 1;
        if (next) next.disabled = list.scrollLeft >= max - 1;
      };
      list.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  if (document.readyState !== 'loading') {
    initSliderChevrons();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initSliderChevrons();
    });
  }

  // Re-init when the theme editor injects/reloads this section.
  document.addEventListener('shopify:section:load', function (e) {
    initSliderChevrons(e.target);
  });
})();
